import {
  Box,
  Button,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { Gear, Info, Pen, Plus, PlusCircle, TextAlignCenter, Trash } from '@phosphor-icons/react';
import { memo, useEffect, useRef, useState } from 'react';
import { Position, useEdges, useReactFlow } from 'reactflow';
import { AuthService } from '../../service/AuthService';
import { useUpdateCompanySpaceMutation } from '../../queries/spaces';
import { useUpdateUserMutation } from '../../queries/me';
import { FlowService } from '../../service/FlowService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { NotificationType, useNotificationStore } from '../../store/storeNotifications';
import { useObjectStore } from '../../store/storeObjects';
import { useSessionStore } from '../../store/storeSessions';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import ObjectModal from '../Layout/LayoutComponents/FluxObjects/ObjectModal';
import SettingsModal from '../Layout/LayoutComponents/FluxObjects/SettingsModal';
import TagInput from '../Test/TagInput';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import InputHandle from '../UI/InputHandle/InputHandle';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';

const ObjectCaller = ({ id, data, type }: NodeProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isObjectModalOpen, setIsObjectModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const getSelectedObject = () => {
    const currentSpaceId = spaceId || 'user';
    return objects[currentSpaceId]?.find(obj => obj?.id === selectedRefObject);
  };

  const [selectedRefObject, setSelectedRefObject] = useState('');
  const { setSchema, spaceId, setSaving, flowId } = useFlowsStore(
    state => state,
  );
  const edges = useEdges();
  const { setNodes: setNodeState } = useReactFlow();
  const { objects, updateObject, resetObjectUpdate, getObjectsBySpaceId, addObject, setObjects } = useObjectStore(state => state);
  const { activeSession, sessions, setActiveSession } = useSessionStore(state => state);

  const [hasToExecute, setHasToExecute] = useState(true);
  const [advancedCollapsed, setAdvancedCollapsed] = useState(true);
  const { setAuthUser } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const updateUserMutation = useUpdateUserMutation();
  const updateCompanySpaceMutation = useUpdateCompanySpaceMutation();

  const syncObjects = async () => {
    const objectsToUpdate = getObjectsBySpaceId(spaceId || 'user');

    if (!spaceId) {
      await updateUserMutation.mutateAsync({ objects: objectsToUpdate });
      const userRes = await AuthService.getMe();
      setAuthUser(userRes?.data);
    } else {
      await updateCompanySpaceMutation.mutateAsync({
        companyId: user?.selectedCompanyId!,
        body: { spaceId, objects: objectsToUpdate },
      });
    }

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      objectCallerData: objectsToUpdate,
      flowTriggerData: checkExecuteTrigger(data, id),
      changeType: 'execute',
      setSchema,
    });
  }

  const onChange = (value: any, name: any) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        [name]: value,
      },
      setSchema,
    });
  };

  const onChangeTextElement = (value: string, id: string) => {
    const object = getSelectedObject();
    if (!object) return;

    const updatedObject = {
      ...object,
      text: object?.text?.map(item => {
        if (item.id === id) {
          return { ...item, text: value };
        }
        return item;
      }),
    };

    updateObject(object.id, updatedObject, spaceId || 'user');
    setIsEditing(true);
  };

  const debounceTimeoutRef = useRef<any>(null);

  const _legacyOnSubmit = async () => {
    try {
      setIsLoading(true);
      const objectsFromStore = getObjectsBySpaceId(spaceId || 'user');

      objectsFromStore?.map(object => {
        if (object.id === selectedRefObject) {
          const findItem = object?.text?.map(item => {
            const checked = data?.[item.id + 'checked'];
            const newTxt = data[item.id + '-in'];

            const text = checked
              ? newTxt?.length > 0
                ? newTxt
                : item?.text
              : newTxt?.length > 0
                ? item.text?.length > 0
                  ? item.text + '\n\n' + newTxt
                  : newTxt
                : item?.text;

            return {
              ...item,
              text: text,
            };
          });

          let updatedObject = {
            ...object,
            text: findItem,
          };

          if (data.sessionEnabled && object.sessions) {
            const sessionIdToUpdate = object.selectedSession;

            const updatedSessions = object.sessions.map(session => {
              if (session.id === sessionIdToUpdate) {
                return {
                  ...session,
                  content: findItem,
                };
              }
              return session;
            });

            const sessionExists = object.sessions.some(s => s.id === sessionIdToUpdate);
            if (!sessionExists && sessionIdToUpdate) {
              updatedSessions.push({
                id: sessionIdToUpdate,
                name: activeSession.name || sessionIdToUpdate,
                content: findItem,
              });
            }

            updatedObject = {
              ...updatedObject,
              sessions: updatedSessions,
              selectedSession: sessionIdToUpdate || object.selectedSession,
            };
          }

          updateObject(object.id, updatedObject, spaceId || 'user');

          return updatedObject;
        }
        return object;
      });

      await syncObjects();

      setTimeout(() => {
        setHasToExecute(true);
      }, 1500);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: 'Error updating object',
      });
    } finally {
      setIsEditing(false);
      setIsLoading(false);
    }
  };

  const addNewElement = () => {
    const newElement = {
      id: Math.random().toString(36).substr(2, 9),
      text: '',
    };

    const object = getSelectedObject();
    if (!object) return;

    const updatedObject = {
      ...object,
      text: [...object.text, newElement],
    };

    updateObject(object.id, updatedObject, spaceId || 'user');
    setIsEditing(true);
  };

  const onRemoveElement = (id: string) => {
    const object = getSelectedObject();
    if (!object) return;

    const updatedObject = {
      ...object,
      text: object.text.filter((text: any) => text.id !== id),
    };

    updateObject(object.id, updatedObject, spaceId || 'user');
    setIsEditing(true);
  };

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onSubmit();
        }, 1000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, data?.canceled]);

  useEffect(() => {
    setSelectedRefObject(data.selectedId || '');
  }, [data?.selectedId]);

  useEffect(() => {
    async function updateSessionContents() {
      if (!data.sessionEnabled) return;

      const selectedObject = getSelectedObject();
      if (!selectedObject) return;

      if (selectedObject.selectedSession === activeSession.id) return;

      setIsLoading(true);

      const sessionBody = selectedObject.sessions?.find(
        s => s.id === selectedObject.selectedSession,
      )?.content;
      const currentContent =
        Array.isArray(selectedObject.text) && selectedObject.text.length > 0
          ? selectedObject.text
          : Array.isArray(sessionBody)
            ? sessionBody
            : selectedObject.text ?? [];

      const emptyContentTemplate = currentContent.map(x => ({ ...x, text: "" }));

      const previousSessionId = selectedObject.selectedSession;

      const existingObjectSessions = selectedObject.sessions || [];
      const objectSessionsMap = new Map(
        existingObjectSessions.map(s => [s.id, { ...s }])
      );

      sessions.forEach(session => {
        const existingSession = objectSessionsMap.get(session.id);

        if (session.id === previousSessionId) {
          objectSessionsMap.set(session.id, {
            ...session,
            content: currentContent,
          });
        } else if (existingSession?.content) {
          const mergedContent = emptyContentTemplate.map(templateItem => {
            const existingItem = existingSession.content.find(item => item.id === templateItem.id);
            return existingItem || templateItem;
          });
          objectSessionsMap.set(session.id, {
            ...session,
            content: mergedContent,
          });
        } else {
          objectSessionsMap.set(session.id, {
            ...session,
            content: emptyContentTemplate,
          });
        }
      });

      const objectSessions = Array.from(objectSessionsMap.values());

      const activeSessionContent = objectSessions.find(s => s.id === activeSession.id)?.content || emptyContentTemplate;

      const newObject = {
        ...selectedObject,
        sessions: objectSessions,
        selectedSession: activeSession.id,
        text: activeSessionContent,
      };

      updateObject(selectedObject.id, newObject, spaceId || 'user');

      try {
        await syncObjects();
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: 'Error updating session contents',
        });
      } finally {
        setIsLoading(false);
      }
    }

    updateSessionContents();
  }, [data.sessionEnabled, activeSession.id, selectedRefObject, spaceId, sessions]);

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const versionId = searchParams.get('versionId') || undefined;
      const response = await FlowService.executeSingleNode(
        flowId,
        id,
        undefined,
        versionId,
      );
      const execId = response?.data?.execId;
      if (!execId) {
        throw new Error('Missing execId in execute-single-node response');
      }
      setNodeState((nds: any) =>
        nds?.map((nd: any) =>
          nd.id === id
            ? { ...nd, data: { ...nd.data, currentExecId: execId } }
            : nd,
        ),
      );
    } catch (error) {
      console.error('execute-single-node failed', {
        nodeId: id,
        flowId,
        error: error instanceof Error ? error.message : String(error),
      });
      setNotification({
        type: NotificationType.Error,
        message: 'Failed to run node',
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const status = data?.executionStatus;
    if (status === 'COMPLETED' || status === 'FAILED') {
      setIsLoading(false);
    }
  }, [data?.executionStatus]);

  const handleRun = async (_run: string) => {
    await onSubmit();
  };

  const changeIsLocal = async () => {
    setIsLoading(true);
    setIsSettingsModalOpen(false);

    const objectUpdate = getSelectedObject();
    if (!objectUpdate) return;

    let selectedId = objectUpdate.id;

    if (!objectUpdate?.localObject) {
      const newObject = {
        ...objectUpdate,
        localObject: true,
        flowId: flowId,
        id: Math.random().toString(36).substr(2, 9),
        name: objectUpdate?.name + ' (Local)',
      };

      addObject(newObject, spaceId || 'user');
      selectedId = newObject.id;
    } else {
      const newObject = {
        ...objectUpdate,
        localObject: false,
        flowId: '',
      };

      updateObject(objectUpdate.id, newObject, spaceId || 'user');
    }

    const objectsToUpdate = getObjectsBySpaceId(spaceId || 'user');

    if (!spaceId) {
      await updateUserMutation.mutateAsync({ objects: objectsToUpdate });

      const userRes = await AuthService.getMe();
      setAuthUser(userRes?.data);
    } else {
      await updateCompanySpaceMutation.mutateAsync({
        companyId: user?.selectedCompanyId!,
        body: { spaceId, objects: objectsToUpdate },
      });
    }

    setNotification({
      type: NotificationType.Success,
      message: 'Object updated successfully',
    });

    onChange(selectedId, 'selectedId');
    setIsLoading(false);
  }

  const saveChanges = async () => {
    try {
      const object = getSelectedObject();
      if (!object) return;

      setIsLoading(true);

      let updatedObject = { ...object };

      if (data.sessionEnabled && object.sessions) {
        const sessionIdToUpdate = object.selectedSession || activeSession.id;

        const updatedSessions = object.sessions.map(session => {
          if (session.id === sessionIdToUpdate) {
            return {
              ...session,
              content: object.text,
            };
          }
          return session;
        });

        const sessionExists = object.sessions.some(s => s.id === sessionIdToUpdate);
        if (!sessionExists) {
          updatedSessions.push({
            id: sessionIdToUpdate,
            name: activeSession.name,
            content: object.text,
          });
        }

        updatedObject = {
          ...object,
          sessions: updatedSessions,
          selectedSession: sessionIdToUpdate,
        };
      }

      updateObject(selectedRefObject, updatedObject, spaceId || 'user');
      await syncObjects();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: 'Error updating object',
      });
    } finally {
      setIsEditing(false);
      setIsLoading(false);
    }
  }

  return (
    <Node
      edges={edges}
      isLoading={isLoading}
      id={id}
      type={type}
      data={data}
      handleRun={handleRun}
      onSubmit={onSubmit}
      btnText="Run"
      isGlobal={true}
      showTokensUsed={false}
    >
      <Box sx={{ p: '16px' }}>
        <Box>
          <Label isAdvanced={true} labelName="Select an Object" />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Select
                name="flowId"
                value={selectedRefObject || 'none'}
                onChange={e => onChange(e.target.value, 'selectedId')}
                className="nodrag"
                size="small"
                sx={{
                  '& .MuiList-root': {
                    backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: 'none',
                  '.MuiOutlinedInput-notchedOutline': { border: 0 },
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  width: '100%'
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                      backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                  },
                }}
              >
                <MenuItem
                  value="none"
                  disabled
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Choose an Object
                </MenuItem>

                {objects
                [spaceId || 'user']
                  ?.filter(obj => !obj.localObject || obj.flowId === flowId)
                  ?.sort((a, b) => (a?.name > b?.name ? 1 : -1))
                  ?.map((object: any) => (
                    <MenuItem
                      key={object.id}
                      value={object.id}
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      {object.name}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
            <Box >
              <IconButtonTooltip
                title="Add new object"
                componentProps={{
                  className: 'nodrag',
                }}
                onClick={event => {
                  event.stopPropagation();
                  resetObjectUpdate();
                  setIsObjectModalOpen(true);
                }}
              >
                <PlusCircle size={16} color="#98A2B3" />
              </IconButtonTooltip>
            </Box>
          </Box>

        </Box>

        {data.sessionEnabled && (
          <Box mt={'8px'}>
            <Label isAdvanced={true} labelName="Select Session" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Select
                name="sessionSelect"
                value={activeSession.id}
                onChange={(e) => {
                  setActiveSession(e.target.value);
                }}
                className="nodrag"
                size="small"
                sx={{
                  '& .MuiList-root': {
                    backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: 'none',
                  '.MuiOutlinedInput-notchedOutline': { border: 0 },
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  width: '100%'
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                      backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                  },
                }}
              >
                {(sessions).map(session => (
                  <MenuItem
                    key={session.id}
                    value={session.id}
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    {session.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        )}

        <Box mt={'24px'} />

        <Box>
          {!selectedRefObject ? (
            <Box
              width={'386px'}
              height={'187px'}
              sx={{
                padding: '10px',
                borderRadius: '8px',
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2398A2B3FF' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <TextAlignCenter size={32} color="#98A2B3" />
                <Typography fontSize={'14px'} color="#98A2B3">
                  Easily inject your objects into workflows.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Label
                  labelName="Object Elements"
                  onCollapse={() => setAdvancedCollapsed(!advancedCollapsed)}
                  sx={{
                    mb: advancedCollapsed ? '24px' : '0',
                  }}
                />

                <Box
                  display={{
                    display: 'flex',
                    justifyContent: 'end',
                    gap: '8px',
                  }}
                >
                  <IconButtonTooltip
                    onClick={() => {
                      setIsSettingsModalOpen(true);
                    }}
                    title={'Object Settings'}
                    componentProps={{
                      className: 'nodrag',
                    }}
                  >
                    <Gear color="#667085" size={20} />
                  </IconButtonTooltip>
                </Box>
              </Box>

              {(() => {
                const selectedObject = getSelectedObject();
                return selectedObject?.text?.map((txt, index) => (
                  <Box key={txt?.id}>
                    <Box position={advancedCollapsed ? 'relative' : 'unset'}>
                      <TagInput
                        className="nodrag"
                        key={txt?.id}
                        value={txt.text}
                        maxLength={500}
                        labelName={`Element ${index + 1}`}
                        isAdvanced={true}
                        nodeId={id}
                        findColor={findColor}
                        advancedCollapsed={advancedCollapsed}
                        nodeLabel={data?.label}
                        handleId={`${txt?.id}-in`}
                        handleSettings={{
                          top: '50%',
                        }}
                        isActive={isActiveEdge(
                          edges,
                          id,
                          `${txt.id}-in`,
                          'target',
                        )}
                        isCollapsed={false}
                        style={{
                          resize: 'both',
                          overflow: 'auto',
                          width: '100%',
                        }}
                        customSettingsLeft={
                          <>
                            <IconButtonTooltip
                              onClick={() => onRemoveElement(txt?.id)}
                              title="Delete Element"
                            >
                              <Trash size={14} />
                            </IconButtonTooltip>
                          </>
                        }
                        customSettingsRight={
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Switch
                                size='small'
                                onChange={e => {
                                  onChange(
                                    e.target.checked,
                                    txt.id + 'clearOnExecution',
                                  );
                                }}
                                checked={!!data?.[txt.id + 'clearOnExecution']}
                              />
                              <IconButtonTooltip title={'Clear output upon run'}>
                                <Info color='#98A2B3' size={12} />
                              </IconButtonTooltip>
                            </Box>
                          </>
                        }
                        customSettingsUnderTitle={
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                              <Typography color='#98A2B3' fontSize={'14px'}>Append</Typography>
                              <Switch
                                name="checkingAddToRefOrOverwrite"
                                size='small'
                                checked={!!data?.[txt.id + 'checked']}
                                onChange={e => {
                                  onChange(
                                    e.target.checked,
                                    txt.id + 'checked',
                                  );
                                }}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#1976d2'
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#1976d2'
                                  },
                                  '& .MuiSwitch-thumb': {
                                    backgroundColor: '#1976d2',
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: '#1976d2',
                                    opacity: 0.5,
                                  },
                                }}
                              />
                              <Typography color='#98A2B3' fontSize={'14px'}>Overwrite</Typography>
                            </Box>
                          </>
                        }
                        onChange={(e: any) => onChangeTextElement(e.target.value, txt?.id)}
                      />

                      <InputHandle
                        handleId={`${txt?.id}-out`}
                        type="source"
                        right={'-26px'}
                        top={'50%'}
                        activeColor={findColor.color}
                        position={Position.Right}
                        isActive={isActiveEdge(
                          edges,
                          id,
                          `${txt.id}-out`,
                          'source',
                        )}
                      />
                    </Box>
                  </Box>
                )) || null;
              })()}
              {selectedRefObject && isEditing && (
                <Box display={'flex'} justifyContent={'center'}>
                  <Button
                    sx={{
                      mt: '20px',
                    }}
                    onClick={saveChanges}
                  >
                    Save Changes <Pen size={20} />
                  </Button>
                </Box>
              )}

              {selectedRefObject && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                  }}
                >
                  <Button onClick={addNewElement}>
                    Add new element <Plus />
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <ObjectModal
        onHide={() => {
          setIsObjectModalOpen(false);
        }}
        selectObject={(objId: string) => {
          onChange(objId, 'selectedId');
        }}
        flowId={flowId}
        show={isObjectModalOpen}
        spaceId={spaceId || 'user'}
      />

      <SettingsModal
        show={isSettingsModalOpen}
        data={data}
        object={getSelectedObject()}
        onHide={() => setIsSettingsModalOpen(false)}
        onConfirm={changeIsLocal}
        onChange={(e: any) => onChange(e.target.value, e.target.name)}
      />
    </Node>
  );
};

export default memo(ObjectCaller);
